// features/tour/TourProvider.tsx
//
// Owns the per-page guided tours. A tour auto-runs the first time a staff
// member lands on a page that has one; it can also be replayed on demand. The
// "seen" state is persisted per page-tour at /users/{uid}/toursSeen/{tourId}.
//
// TESTING: set TOUR_ALWAYS = true to re-show every page tour on every visit
// (including refresh), so the experience can be re-checked without clearing the
// seen markers. Set it back to false for production so each tour shows once.

import {
  createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode, ReactElement,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, get, update } from 'firebase/database';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { PageTour, pageTourForPath } from './tourSteps';

// MASTER SWITCH: tutorials are OFF for launch. When false, nothing auto-runs,
// the manual "Take a Tour" launchers are hidden, and start() is a no-op.
// Flip to true to re-enable the whole tutorial system.
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export const TOURS_ENABLED: boolean = false;
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
const TOUR_ALWAYS: boolean = false; // (only relevant when TOURS_ENABLED) re-show every visit

// Small delay after a navigation so the page can render before we start.
const START_DELAY_MS = 650;

interface TourContextValue {
  /** The page tour currently running, or null. */
  active: PageTour | null;
  /** Current step index within the active tour. */
  index: number;
  /** Replay the tour for the current page (or jump to the dashboard tour). */
  start: () => void;
  stop: () => void;
  next: () => void;
  prev: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
}

export function TourProvider({ children }: { children: ReactNode }): ReactElement {
  const { user, isStaff, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [active, setActive] = useState<PageTour | null>(null);
  const [index, setIndex] = useState(0);

  // Page tours auto-run for staff-role accounts (judges etc.) + admins.
  const staffLike = isStaff() || isAdmin();
  // Don't auto-start the same tour twice in one session (avoids races while the
  // "seen" write is in flight). Ignored when TOUR_ALWAYS is on.
  const autoStartedRef = useRef<Set<string>>(new Set());

  const markSeen = useCallback((tourId: string) => {
    if (!user?.uid) return;
    update(ref(db, `users/${user.uid}/toursSeen`), { [tourId]: Date.now() }).catch(() => undefined);
  }, [user?.uid]);

  const startTour = useCallback((tour: PageTour) => {
    setActive(tour);
    setIndex(0);
  }, []);

  const stop = useCallback(() => {
    setActive((cur) => { if (cur) markSeen(cur.id); return null; });
    setIndex(0);
  }, [markSeen]);

  const next = useCallback(() => {
    setActive((cur) => {
      if (!cur) return cur;
      setIndex((i) => {
        if (i >= cur.steps.length - 1) {
          markSeen(cur.id);
          setActive(null);
          return 0;
        }
        return i + 1;
      });
      return cur;
    });
  }, [markSeen]);

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Manual replay: run the current page's tour, or send them to the dashboard.
  const start = useCallback(() => {
    if (!TOURS_ENABLED) return;
    const tour = pageTourForPath(location.pathname);
    if (tour) startTour(tour);
    else navigate('/landing');
  }, [location.pathname, navigate, startTour]);

  // Auto-run the first time a staff member lands on a page with a tour.
  useEffect(() => {
    if (!TOURS_ENABLED) return; // tutorials off
    if (!user?.uid) return;
    // Staff get auto-tours everywhere; anyone (e.g. players) gets them on the
    // dedicated /tutorial/* practice routes they navigated to intentionally.
    const onTutorialRoute = location.pathname.startsWith('/tutorial/');
    if (!staffLike && !onTutorialRoute) return;
    if (active) return;
    const tour = pageTourForPath(location.pathname);
    if (!tour) return;
    if (tour.autoRun === false) return; // pointer tours only run via "Take a Tour"
    if (!TOUR_ALWAYS && autoStartedRef.current.has(tour.id)) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const begin = () => {
      timer = setTimeout(() => {
        if (cancelled) return;
        autoStartedRef.current.add(tour.id);
        startTour(tour);
      }, START_DELAY_MS);
    };

    if (TOUR_ALWAYS) {
      begin();
    } else {
      get(ref(db, `users/${user.uid}/toursSeen/${tour.id}`))
        .then((snap) => { if (!cancelled && !snap.exists()) begin(); })
        .catch(() => undefined);
    }

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.uid, staffLike]);

  const value: TourContextValue = { active, index, start, stop, next, prev };
  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
